import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Button,
	Box,
	IconButton,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	StaffFilter,
	StaffListSummaryItem,
} from '@/api/DTO/staff/staffList.interface';
import StaffListSummaryFilterSection from './components/DeleteUserRecordSummaryFilterSection';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { useGetStaffPageQuery } from '@/api/staff/staffApiSlice';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import {
	DISPLAY_CHINESE_DATE_TIME_FORMAT,
	DISPLAY_DATETIME_12H_FORMAT,
} from '@/utils/dateHelper';
import dayjs from 'dayjs';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useGetDeleteUserListQuery } from '@/api/user/userApiSlice';
import {
	DeleteUserFilter,
	DeleteUserPageResDTO,
} from '@/api/DTO/user/userList.interface';
import { showLabel } from '@/utils/helper';

export interface DeleteUserListDataGrid {
	removedAt: string;
}

export default function DeleteUserRecordSummaryPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);

	const removeUserMethodList = [
		{ id: 1, name: t('user.staffDelete') },
		{ id: 0, name: t('user.appDelete') },
	];

	// useGetUser返回 userInfo 和 userRoles
	// 接收一个对象
	// 第一个入参shouldRefetch：是否再次调用获取用户信息api
	// 第二个入参roleObj：传入需要判断的权限返回权限对象boolean
	// roleObj的key自定义，值为需要判断的权限，返回自定义key和对应的boolean
	// const { userRoles } = useGetUser<useGetUserResultType>({
	// 	shouldRefetch: true,
	// 	roleObj: {
	// 		create: ['create', 'staffList'],
	// 	},
	// });

	const defaultPropertyFilter: DeleteUserFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<DeleteUserFilter> = defaultPropertyFilter;
	const methods = useForm<DeleteUserFilter>({
		defaultValues,
	});
	const transformedSearchParams: DeleteUserFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetDeleteUserListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: DeleteUserPageResDTO[]) => {
		if (response == null) return;
		const newResponse: DeleteUserListDataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				removedAt: !item.removedAt
					? '-'
					: dayjs(item.removedAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as DeleteUserFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'username',
			headerName: t('user.userName'),
			flex: 21,
		},
		{
			field: 'phone',
			headerName: t('common.phoneNumber'),
			flex: 15,
		},
		{
			field: 'email',
			headerName: t('common.email'),
			flex: 21,
		},
		{
			field: 'removeReason',
			headerName: t('user.deleteReason'),
			flex: 21,
		},
		{
			field: 'removeUserMethod',
			headerName: t('user.deletePath'),
			flex: 21,
			renderCell: (params: any) =>
				showLabel(params.row.removeUserMethod, removeUserMethodList),
		},
		{
			field: 'removeStaffName',
			headerName: t('user.functionary'),
			flex: 21,
		},
		{
			field: 'removedAt',
			headerName: t('user.deleteTime'),
			flex: 21,
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.deleteUserRecord')}
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<StaffListSummaryFilterSection></StaffListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
